// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";

// zoom-settings imports
import { IContainer, IProps, IState } from "./ZoomSettings.d";
import { zoomSettingsFragments, query } from "./ZoomSettings.gql";
import { styles } from "./ZoomSettings.styles";
// other imports
import { ZoomButton } from "@components/zoom-button";
import { H2, A, P } from "@ui";
// state
import { IReduxState } from "@reducers/root.reducer";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { deleteZoomAccount } from "@mutations";

// util
import { articleUrls } from "@clockwise/client-commons/src/constants/help-center";
import { fromGlobalId } from "@util/graphql.util";
import { logger } from "@util/logger.util";
import { track, TrackingEvents } from "@util/analytics.util";

// material-ui
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@material-ui/core";
import { Delete } from "@material-ui/icons";
import withStyles from "@material-ui/core/styles/withStyles";

import { greys } from "@clockwise/web-commons/src/styles/color.styles";

///////////////
// Component //
///////////////
class ZoomSettingsCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      showDeleteConfirmationModal: false,
    };
  }

  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-
  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    if (
      !!this.maybeGetZoomAccountFromOrg(nextProps.org) &&
      !this.maybeGetZoomAccountFromOrg(this.props.org)
    ) {
      track(TrackingEvents.ZOOM.ADDED);
    }
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private maybeGetZoomAccountFromOrg = (org: ISchema.IOrg) => {
    if (!org.zoomAccount) {
      return undefined;
    } else if (org.zoomAccount.__typename !== "ZoomAccount") {
      logger.error("Failed to fetch zoom account");
      return undefined;
    }

    return org.zoomAccount;
  };

  // ~-~-~-~-~-~-~-
  // Callbacks
  // ~-~-~-~-~-~-~-
  private onAuthPopupClosed = () => {
    this.props.relay.refetch({}, undefined, undefined, { force: true });
  };

  private onLearnMore = () => {
    track(TrackingEvents.HELP.ZOOM);
  };

  private onDeleteConfirmationModalOpen = () => {
    this.setState({ showDeleteConfirmationModal: true });
  };

  private onDeleteConfirmationModalClose = () => {
    this.setState({ showDeleteConfirmationModal: false });
  };

  private onClickConfirmDelete = (zoomAccount: ISchema.IZoomAccount) => {
    const { org, relay } = this.props;

    track(TrackingEvents.ZOOM.DELETE);
    deleteZoomAccount(
      relay.environment,
      { orgRelayId: org.id, zoomAccountId: zoomAccount.accountId },
      () => {
        this.props.snackBarOpen("Deleted your Zoom integration!");
        this.onDeleteConfirmationModalClose();
        this.props.relay.refetch({}, undefined, undefined, { force: true });
      },
      () => {
        const msg = `Failed to delete Zoom integration`;
        logger.error(msg);
        this.props.snackBarOpen(msg);
      },
    );
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private maybeRenderDeleteConfirmationModal = (zoomAccount: ISchema.IZoomAccount) => {
    const { classes } = this.props;
    const { showDeleteConfirmationModal } = this.state;

    return (
      <Dialog
        open={showDeleteConfirmationModal}
        onClose={this.onDeleteConfirmationModalClose}
        PaperProps={{ style: { maxWidth: 700, width: 700, textAlign: "center" } }}
      >
        <DialogTitle>
          <H2 style={{ fontSize: 22, marginBottom: 0, paddingBottom: 0 }}>
            Are you sure you want to delete your Zoom integration?
          </H2>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 0, paddingBottom: 0 }}>
          <P
            style={{
              fontSize: 15,
              lineHeight: 1.8,
              color: greys.slate,
            }}
          >
            If you delete this integration, you will no longer be able to schedule with Zoom with
            Clockwise.
          </P>
        </DialogContent>
        <DialogActions>
          <Button
            // keyboardFocused={true}
            variant="text"
            onClick={this.onDeleteConfirmationModalClose}
          >
            Cancel
          </Button>
          <Button
            variant="text"
            onClick={() => this.onClickConfirmDelete(zoomAccount)}
            classes={{ root: classes.deleteButton }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  private renderZoomAuthFlow = () => {
    const { classes } = this.props;

    return (
      <div className={classes.zoomAuthContainer}>
        <ZoomButton onAuthPopupClosed={this.onAuthPopupClosed} />
      </div>
    );
  };

  private renderZoomSettings = (zoomAccount: ISchema.IZoomAccount) => {
    const { user, classes } = this.props;
    const userId = fromGlobalId(user.id).id;
    const isConnectingUserCurrentUser = userId === zoomAccount.ownerUserId;

    const ownerPerson =
      zoomAccount.ownerPersons.__typename === "PersonList"
        ? zoomAccount.ownerPersons.list[0]
        : null;
    const zoomAccountOwnerName =
      ownerPerson && ownerPerson.isYou
        ? "You"
        : ownerPerson && ownerPerson.profile
        ? `${ownerPerson.profile.givenName} ${ownerPerson.profile.familyName}`
        : ownerPerson
        ? ownerPerson.primaryEmail
        : "";

    if (!zoomAccountOwnerName) {
      logger.error("failed to render zoom account owner name");
    }

    return (
      <div className={classes.zoomAccountContainer}>
        <div className={classes.zoomAccountContent}>
          <div className={classes.zoomAccountNameInfo}>
            <div className={classes.zoomAccountName}>
              {isConnectingUserCurrentUser
                ? `You ${
                    ownerPerson ? `(${ownerPerson.primaryEmail})` : ""
                  } have successfully set up your Zoom integration!`
                : `Your Zoom integration has been set up. Please contact ${zoomAccountOwnerName} ${
                    ownerPerson ? `(${ownerPerson.primaryEmail})` : ""
                  } if you have any concerns.`}
            </div>
            <div className={classes.zoomAccountDomain}>
              Zoom account domain: {zoomAccount.displayName}
            </div>
          </div>
          {isConnectingUserCurrentUser ? (
            <IconButton onClick={this.onDeleteConfirmationModalOpen}>
              <Delete classes={{ root: classes.deleteIcon }} />
            </IconButton>
          ) : (
            ""
          )}
        </div>
        {/* <Button
          variant="text"
          onClick={this.onDeleteConfirmationModalOpen}
          classes={{ root: classes.deleteTeamButton }}
        >
          Delete team
        </Button> */}
      </div>
    );
  };

  public render() {
    const { org, classes } = this.props;
    const zoomAccount = this.maybeGetZoomAccountFromOrg(org);

    return (
      <div className={classes.container}>
        <H2 style={{ paddingTop: 20 }}>Zoom admin integration</H2>
        <div className={classes.intro}>
          Connect your organization’s Zoom admin account to Clockwise to enable Zoom scheduling for
          everyone. Once set up, every user that has a Zoom account matching their GSuite email
          address can schedule a Zoom meeting. Note: only your Zoom admin can set this up.{" "}
          <A target="_blank" href={articleUrls.zoom} onClick={this.onLearnMore}>
            Learn more.
          </A>
        </div>
        {zoomAccount ? this.renderZoomSettings(zoomAccount) : this.renderZoomAuthFlow()}
        {zoomAccount ? this.maybeRenderDeleteConfirmationModal(zoomAccount) : ""}
      </div>
    );
  }
}

/////////////////
// With Styles //
/////////////////
export const ZoomSettingsStyled = withStyles(styles)(ZoomSettingsCmpt);

///////////
// Redux //
///////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const ZoomSettingsRedux = connect(mapStateToProps, {
  snackBarOpen,
})(ZoomSettingsStyled);

///////////
// Relay //
///////////
export const ZoomSettings = createRefetchContainer<IContainer>(
  ZoomSettingsRedux,
  zoomSettingsFragments,
  query,
);
